import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Settings2, RefreshCw, TrendingUp, Wallet, AlertTriangle, X, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPut } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import { getRole, logoutUser } from "../../utils/authService";
import { toast } from "../../utils/toast";
import Skeleton from "../common/Skeleton";
import { PillSelect } from "../common/ui.jsx";
import "./cfo-dashboard.css";

const WORK_DAYS_PER_YEAR = 260;
const DEFAULT_WORK_HOURS = 8;
const toYmd = (dateObj) => dateObj.toISOString().split("T")[0];

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthRange = (monthValue) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(monthValue || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;

  const start = new Date(Date.UTC(year, month - 1, 1));
  const end = new Date(Date.UTC(year, month, 0));
  return { start: toYmd(start), end: toYmd(end) };
};

// Native <input type="month"> renders as an uninteractive raw-text box in
// browsers without a built-in picker (Firefox in particular) — swap it for
// plain <select> dropdowns instead.
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

const parseMonthValue = (value) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(value || ""));
  return match ? { year: Number(match[1]), month: Number(match[2]) } : { year: new Date().getFullYear(), month: 1 };
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value || 0);

const formatPct = (value) => `${Number(value || 0).toFixed(1)}%`;

const getProductivityClass = (value) => {
  const v = Number(value || 0);
  if (v >= 75) return "prod-high";
  if (v >= 50) return "prod-medium";
  return "prod-low";
};

const getWorkMode = (utilization) => {
  const u = Number(utilization || 0);
  if (u > 100) return { label: "Overtime", className: "mode-overtime" };
  if (u < 80) return { label: "Underutilized", className: "mode-under" };
  return { label: "Balanced", className: "mode-balanced" };
};

const getTeamUtilClass = (value) => {
  const v = Number(value || 0);
  if (v >= 75) return "team-util-high";
  if (v >= 50) return "team-util-medium";
  return "team-util-low";
};

const getSalaryStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoAgentConfigMonthly_v1_${tenantId}`;
};

const getLegacyMonthlySalaryStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoSalaryConfigMonthly_v1_${tenantId}`;
};

const getLegacyAnnualSalaryStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoSalaryConfig_v1_${tenantId}`;
};

const readSalaryMapFromLocalStorage = () => {
  try {
    const agentConfig = JSON.parse(localStorage.getItem(getSalaryStorageKey()) || "{}");
    if (agentConfig && Object.keys(agentConfig).length > 0) return agentConfig;

    const monthly = JSON.parse(localStorage.getItem(getLegacyMonthlySalaryStorageKey()) || "{}");
    if (monthly && Object.keys(monthly).length > 0) {
      const migrated = {};
      Object.keys(monthly).forEach((k) => {
        migrated[k] = { teamName: "", monthlySalary: Number(monthly[k] || 0) };
      });
      return migrated;
    }

    const legacyAnnual = JSON.parse(localStorage.getItem(getLegacyAnnualSalaryStorageKey()) || "{}");
    const converted = {};
    Object.keys(legacyAnnual || {}).forEach((k) => {
      converted[k] = { teamName: "", monthlySalary: Number(legacyAnnual[k] || 0) / 12 };
    });
    return converted;
  } catch {
    return {};
  }
};

const normalizeTeam = (name) => {
  const t = String(name || "").trim();
  return t || "Unassigned";
};

const getMonthLabel = (value) => {
  const range = getMonthRange(value);
  if (!range) return "Selected Month";
  const d = new Date(`${range.start}T00:00:00`);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const CFODashboard = () => {
  const navigate = useNavigate();
  const role = getRole();
  const isCfoKiosk = role === "cfo";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // grid | details
  const [monthlySelection, setMonthlySelection] = useState(() => getCurrentMonth());
  const [salaryMap, setSalaryMap] = useState(() => readSalaryMapFromLocalStorage());

  const [showConfig, setShowConfig] = useState(false);
  const [configEmployees, setConfigEmployees] = useState([]);
  const [configAgentConfig, setConfigAgentConfig] = useState({});
  const [configTeams, setConfigTeams] = useState([]);
  const [bulkSalary, setBulkSalary] = useState(50000);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const configuredEditCount = useMemo(
    () => configEmployees.filter((e) => Number(configAgentConfig[e.agentId]?.monthlySalary || 0) > 0).length,
    [configEmployees, configAgentConfig]
  );

  const openConfigPanel = async () => {
    setShowConfig(true);
    setConfigLoading(true);
    try {
      const [agentsRes, configRes, deptsRes] = await Promise.all([
        apiGet("/api/agents"),
        apiGet("/api/cfo-config"),
        apiGet("/api/departments"),
      ]);
      const agentsData = await agentsRes.json().catch(() => null);
      if (!agentsRes.ok || !Array.isArray(agentsData)) {
        toast.error("Failed to load agents");
        return;
      }
      const normalized = agentsData
        .map((a) => ({ agentId: a.agentId, hostname: a.hostname || "", name: a.name || "" }))
        .filter((a) => !!a.agentId)
        .sort((a, b) => (a.name || a.hostname || a.agentId).toLowerCase().localeCompare((b.name || b.hostname || b.agentId).toLowerCase()));
      setConfigEmployees(normalized);

      const configData = await configRes.json().catch(() => null);
      let loadedConfig = {};
      if (configRes.ok && configData?.success && configData.salaryMap) {
        loadedConfig = configData.salaryMap;
      } else {
        loadedConfig = { ...salaryMap };
      }

      const deptsData = await deptsRes.json().catch(() => null);
      const deptByAgent = {};
      let deptNames = [];
      if (deptsRes.ok) {
        const deptsList = deptsData?.departments || (Array.isArray(deptsData) ? deptsData : []);
        deptNames = deptsList.map((d) => d.name || d).filter(Boolean);
        deptsList.forEach((d) => {
          const deptName = d.name || "";
          if (!deptName) return;
          (d.agents || []).forEach((agentId) => { deptByAgent[agentId] = deptName; });
        });
      }

      const mergedTeams = Array.from(new Set([
        ...deptNames,
        ...Object.values(loadedConfig).map((r) => String(r?.teamName || "").trim()).filter(Boolean),
      ])).sort((a, b) => a.localeCompare(b));
      setConfigTeams(mergedTeams);

      const next = { ...loadedConfig };
      normalized.forEach((emp) => {
        const existing = next[emp.agentId];
        const assignedTeam = deptByAgent[emp.agentId];
        if (!existing?.teamName && assignedTeam) {
          next[emp.agentId] = { teamName: assignedTeam, monthlySalary: Number(existing?.monthlySalary || 0) };
        }
      });
      setConfigAgentConfig(next);
    } catch {
      toast.error("Failed to load agents");
    } finally {
      setConfigLoading(false);
    }
  };

  const applySalaryToAll = () => {
    const value = Number(bulkSalary || 0);
    setConfigAgentConfig((prev) => {
      const next = { ...prev };
      configEmployees.forEach((e) => {
        next[e.agentId] = { teamName: String(next[e.agentId]?.teamName || ""), monthlySalary: value };
      });
      return next;
    });
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      const res = await apiPut("/api/cfo-config", { agentConfig: configAgentConfig, teams: configTeams });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to save configuration");
        return;
      }
      localStorage.setItem(getSalaryStorageKey(), JSON.stringify(configAgentConfig));
      setSalaryMap({ ...configAgentConfig });
      toast.success("Salary configuration saved");
      setShowConfig(false);
    } catch {
      toast.error("Failed to save configuration");
    } finally {
      setConfigSaving(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    const monthRange = getMonthRange(monthlySelection);
    if (!monthRange) return null;
    params.set("filter", "custom");
    params.set("start", monthRange.start);
    params.set("end", monthRange.end);
    return params.toString();
  };

  const fetchDashboard = async () => {
    const query = buildQuery();
    if (!query) {
      toast.error("Invalid date range");
      return;
    }
    setLoading(true);
    try {
      const [res, configRes] = await Promise.all([
        apiGet(`/api/analytics/ceo-dashboard?${query}`),
        apiGet("/api/cfo-config"),
      ]);
      const result = await res.json().catch(() => null);
      if (!res.ok || !result?.success) {
        toast.error(result?.message || "Failed to load CFO dashboard");
        return;
      }
      setData(result);

      const configData = await configRes.json().catch(() => null);
      if (configRes.ok && configData?.success && configData.salaryMap && typeof configData.salaryMap === "object") {
        setSalaryMap(configData.salaryMap);
        localStorage.setItem(getSalaryStorageKey(), JSON.stringify(configData.salaryMap));
      }
    } catch (err) {
      console.error("CFO dashboard fetch error:", err);
      toast.error("Failed to load CFO dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [monthlySelection]);

  const summary = data?.summary || {};
  const employees = data?.employees || [];

  const finance = useMemo(() => {
    const workHoursPerDay = Number(summary.workHoursPerDay || DEFAULT_WORK_HOURS);
    const monthlyHours = Math.max(1, (workHoursPerDay * WORK_DAYS_PER_YEAR) / 12);

    const rows = employees.map((emp) => {
      const monthlySalary = Number(salaryMap?.[emp.agentId]?.monthlySalary || 0);
      const teamName = String(salaryMap?.[emp.agentId]?.teamName || "");
      const hourlyRate = monthlySalary > 0 ? monthlySalary / monthlyHours : 0;
      const trackedActiveMs = Number(emp.trackedActiveMs || 0);
      const trackedIdleMs = Number(emp.trackedIdleMs || 0);
      const productiveMs = Number(emp.productiveTimeMs || 0);
      const utilizedMoney = (productiveMs / 3600000) * hourlyRate;
      const productiveHours = productiveMs / 3600000;
      const productivityPct = trackedActiveMs > 0 ? (productiveMs / trackedActiveMs) * 100 : Number(emp.productivityPct || 0);
      const utilizationPct = Number(emp.utilization || 0);
      const workMode = getWorkMode(utilizationPct);
      const team = normalizeTeam(teamName);

      return {
        agentId: emp.agentId,
        name: displayName(emp),
        teamName,
        team,
        monthlySalary,
        trackedActiveMs,
        trackedIdleMs,
        productiveMs,
        productiveHours,
        utilizedMoney,
        productivityPct,
        utilizationPct,
        workMode,
      };
    });

    const configuredRows = rows.filter((r) => r.monthlySalary > 0);
    const totalSalary = configuredRows.reduce((sum, r) => sum + r.monthlySalary, 0);
    const totalUtilized = configuredRows.reduce((sum, r) => sum + r.utilizedMoney, 0);
    const totalUnutilizedSalary = Math.max(0, totalSalary - totalUtilized);

    return {
      rows: configuredRows.sort((a, b) => b.utilizedMoney - a.utilizedMoney),
      configuredCount: configuredRows.length,
      totalUnutilizedSalary,
      totalSalary,
      totalUtilized,
    };
  }, [employees, salaryMap, summary.workHoursPerDay]);

  const teamRows = useMemo(() => {
    const teamMap = new Map();
    finance.rows.forEach((row) => {
      const key = row.team;
      const prev = teamMap.get(key) || { team: key, employees: 0, allocatedSalary: 0, productive: 0 };
      prev.employees += 1;
      prev.allocatedSalary += Number(row.monthlySalary || 0);
      prev.productive += Number(row.utilizedMoney || 0);
      teamMap.set(key, prev);
    });
    return Array.from(teamMap.values())
      .map((row) => {
        const utilizationPct = row.allocatedSalary > 0 ? (row.productive / row.allocatedSalary) * 100 : 0;
        return {
          ...row,
          utilizationPct,
          unutilized: Math.max(0, row.allocatedSalary - row.productive),
        };
      })
      .sort((a, b) => b.allocatedSalary - a.allocatedSalary);
  }, [finance.rows]);

  const hasConfiguredData = Number(finance.configuredCount || 0) > 0;

  useEffect(() => {
    if (selectedTeam === "all") return;
    if (!teamRows.some((t) => t.team === selectedTeam)) {
      setSelectedTeam("all");
    }
  }, [teamRows, selectedTeam]);

  useEffect(() => {
    // Always land on teams grid whenever month changes
    setViewMode("grid");
    setSelectedTeam("all");
    setSelectedAgent("all");
  }, [monthlySelection]);

  const agentOptions = useMemo(() => {
    const rows = selectedTeam === "all" ? finance.rows : finance.rows.filter((r) => r.team === selectedTeam);
    return rows
      .map((r) => ({ agentId: r.agentId, name: r.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [finance.rows, selectedTeam]);

  useEffect(() => {
    if (selectedAgent === "all") return;
    if (!agentOptions.some((a) => a.agentId === selectedAgent)) {
      setSelectedAgent("all");
    }
  }, [agentOptions, selectedAgent]);

  const detailRows = useMemo(() => {
    let rows = selectedTeam === "all" ? finance.rows : finance.rows.filter((r) => r.team === selectedTeam);
    if (selectedAgent !== "all") rows = rows.filter((r) => r.agentId === selectedAgent);
    return rows;
  }, [finance.rows, selectedTeam, selectedAgent]);

  const overallUtilizationPct = finance.totalSalary > 0 ? (finance.totalUtilized / finance.totalSalary) * 100 : 0;
  const monthLabel = getMonthLabel(monthlySelection);

  return (
    <div className="cfo-dashboard cfo-only-config">
      <div className="cfo-header-block">
        <div>
          <h1 className="cfo-title">Team Investment Dashboard</h1>
          <p className="cfo-subtitle">{monthLabel} | {teamRows.length} teams | {finance.configuredCount} configured agents</p>
        </div>
        <section className="cfo-filters">
          <PillSelect
            value={parseMonthValue(monthlySelection).year}
            onChange={(e) => setMonthlySelection(`${e.target.value}-${String(parseMonthValue(monthlySelection).month).padStart(2, "0")}`)}
          >
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </PillSelect>
          <PillSelect
            value={parseMonthValue(monthlySelection).month}
            onChange={(e) => setMonthlySelection(`${parseMonthValue(monthlySelection).year}-${String(e.target.value).padStart(2, "0")}`)}
          >
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </PillSelect>
        </section>
        <div className="cfo-topbar">
          <button type="button" className="cfo-secondary-btn cfo-btn-primary" onClick={openConfigPanel}>
            <Settings2 size={16} />
            Configure Salaries
          </button>
          <button type="button" className="gc-icon-btn" onClick={fetchDashboard} data-tip="Refresh">
            <RefreshCw size={14} />
          </button>
          {isCfoKiosk && (
            <button type="button" className="cfo-secondary-btn cfo-btn-muted" onClick={handleLogout}>
              Logout
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="cfo-dashboard-skeleton" style={{ padding: '0 0 20px 0' }}>
          <div className="cfo-kpis" style={{ marginBottom: '20px' }}>
            <Skeleton type="card" height="90px" />
            <Skeleton type="card" height="90px" />
            <Skeleton type="card" height="90px" />
            <Skeleton type="card" height="90px" />
          </div>
          <div className="cfo-panel" style={{ marginBottom: '20px' }}>
            <Skeleton type="rect" height="40px" />
          </div>
          <div className="cfo-team-grid">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} type="card" height="200px" />)}
          </div>
        </div>
      ) : (
        <>
          <section className="cfo-kpis">
            <article className="cfo-kpi-card">
              <span className="cfo-kpi-icon"><Wallet size={16} /></span>
              <div>
                <div className="cfo-kpi-label">Monthly Salary</div>
                <div className="cfo-kpi-value">{formatMoney(finance.totalSalary)}</div>
              </div>
            </article>
            <article className="cfo-kpi-card">
              <span className="cfo-kpi-icon success"><TrendingUp size={16} /></span>
              <div>
                <div className="cfo-kpi-label">Productive Spend</div>
                <div className="cfo-kpi-value">{formatMoney(finance.totalUtilized)}</div>
              </div>
            </article>
            <article className="cfo-kpi-card">
              <span className="cfo-kpi-icon warning"><AlertTriangle size={16} /></span>
              <div>
                <div className="cfo-kpi-label">Total Unutilized Salary</div>
                <div className="cfo-kpi-value">{formatMoney(finance.totalUnutilizedSalary)}</div>
              </div>
            </article>
          </section>

          <section className="cfo-health">
            <div className="cfo-health-row">
              <span>Overall utilization rate</span>
              <strong>{formatPct(overallUtilizationPct)}</strong>
            </div>
            <div className="cfo-health-bar">
              <div className={`cfo-health-fill ${getTeamUtilClass(overallUtilizationPct)}`} style={{ width: `${Math.min(100, overallUtilizationPct)}%` }} />
            </div>
          </section>

          {viewMode === "grid" ? (
            <section className="cfo-panel">
              <h3>Team Investment Overview</h3>
              <div className="cfo-team-grid">
                {teamRows.map((team) => (
                  <button
                    key={team.team}
                    type="button"
                    className="cfo-team-card"
                    onClick={() => {
                      setSelectedTeam(team.team);
                      setSelectedAgent("all");
                      setViewMode("details");
                    }}
                  >
                    <div className="team-head">
                      <div className="team-name">{team.team}</div>
                      <div className="team-count">{team.employees} employees</div>
                    </div>
                    <div className="team-invested">{formatMoney(team.allocatedSalary)}</div>
                    <div className="team-meta">Monthly Salary</div>
                    <div className="team-metrics">
                      <div className="team-util-row">
                        <span>Productive</span>
                        <strong>{formatMoney(team.productive)}</strong>
                      </div>
                      <div className="team-util-row">
                        <span>Unutilized</span>
                        <strong>{formatMoney(team.unutilized)}</strong>
                      </div>
                    </div>
                    <div className="team-progress-track">
                      <div
                        className={`team-progress-fill ${getTeamUtilClass(team.utilizationPct)}`}
                        style={{ width: `${Math.min(100, team.utilizationPct)}%` }}
                      />
                    </div>
                    <div className={`team-util-pill ${getTeamUtilClass(team.utilizationPct)}`}>
                      Utilized {formatPct(team.utilizationPct)}
                    </div>
                  </button>
                ))}
                {teamRows.length === 0 && (
                  <div className="cfo-empty">No teams available for this month.</div>
                )}
              </div>
            </section>
          ) : (
            <>
              <section className="cfo-panel cfo-individual-panel">
                <div className="cfo-individual-header">
                  <h3>{selectedTeam === "all" ? "Employee Details" : `${selectedTeam} - Employee Details`}</h3>
                  <button type="button" className="cfo-secondary-btn cfo-btn-muted" onClick={() => setViewMode("grid")}>
                    Back to Teams Grid
                  </button>
                </div>
                <div className="cfo-detail-filters">
                  <label>
                    Team
                    <select
                      value={selectedTeam}
                      onChange={(e) => {
                        setSelectedTeam(e.target.value);
                        setSelectedAgent("all");
                      }}
                    >
                      <option value="all">All Teams</option>
                      {teamRows.map((row) => (
                        <option key={row.team} value={row.team}>
                          {row.team}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Individual
                    <select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}>
                      <option value="all">All Individuals</option>
                      {agentOptions.map((agent) => (
                        <option key={agent.agentId} value={agent.agentId}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="cfo-table-wrap">
                  <table className="cfo-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Team</th>
                        <th>Monthly Salary Package</th>
                        <th>Productive Spend</th>
                        <th>Productive %</th>
                        <th>Utilization Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRows.map((row) => (
                        <tr key={row.agentId}>
                          <td>{row.name}</td>
                          <td>{row.team}</td>
                          <td>{formatMoney(row.monthlySalary)}</td>
                          <td>{formatMoney(row.utilizedMoney)}</td>
                          <td>
                            <span className={`cfo-pill ${getProductivityClass(row.productivityPct)}`}>
                              {formatPct(row.productivityPct)}
                            </span>
                          </td>
                          <td>
                            <span className={`cfo-pill ${row.workMode.className}`}>
                              {row.workMode.label}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {detailRows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="cfo-empty-row">No employee data for this team.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
      {showConfig && createPortal(
        <div className="cfo-config-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowConfig(false); }}>
          <div className="cfo-config-drawer">
            <div className="cfo-config-drawer-header">
              <div className="cfo-config-drawer-header-text">
                <div className="cfo-config-drawer-title">
                  <span className="cfo-config-drawer-title-icon"><Settings2 size={14} /></span>
                  <h2>Configure Salaries</h2>
                </div>
                <p>Set team and monthly salary for each employee.</p>
              </div>
              <button type="button" className="cfo-config-drawer-close" onClick={() => setShowConfig(false)}>
                <X size={16} />
              </button>
            </div>

            <div className="cfo-config-drawer-toolbar">
              <span className="cfo-config-count">
                {configLoading ? "Loading…" : (<>Salary set: <strong>{configuredEditCount}/{configEmployees.length}</strong></>)}
              </span>
              <label className="cfo-config-bulk-label">
                Apply to all (INR/month)
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={bulkSalary}
                  onChange={(e) => setBulkSalary(Number(e.target.value || 0))}
                />
              </label>
              <button type="button" className="cfo-config-btn-apply" onClick={applySalaryToAll} disabled={configLoading}>
                Apply
              </button>
            </div>

            <div className="cfo-config-drawer-body">
              {configLoading ? (
                <div className="cfo-config-empty">Loading agents…</div>
              ) : (
                <div className="cfo-config-table-wrap">
                  <table className="cfo-config-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Team</th>
                        <th>Monthly Salary (INR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configEmployees.map((emp) => (
                        <tr key={emp.agentId}>
                          <td>{displayName(emp)}</td>
                          <td>
                            <select
                              value={String(configAgentConfig[emp.agentId]?.teamName || "")}
                              onChange={(e) => {
                                const val = e.target.value;
                                setConfigAgentConfig((prev) => ({
                                  ...prev,
                                  [emp.agentId]: { teamName: val, monthlySalary: Number(prev[emp.agentId]?.monthlySalary || 0) },
                                }));
                              }}
                            >
                              <option value="">Select Team</option>
                              {configTeams.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="1000"
                              value={Number(configAgentConfig[emp.agentId]?.monthlySalary || 0)}
                              onChange={(e) => {
                                const val = Number(e.target.value || 0);
                                setConfigAgentConfig((prev) => ({
                                  ...prev,
                                  [emp.agentId]: { teamName: String(prev[emp.agentId]?.teamName || ""), monthlySalary: val },
                                }));
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                      {configEmployees.length === 0 && (
                        <tr><td colSpan={3} className="cfo-config-empty">No agents found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="cfo-config-drawer-footer">
              <button type="button" className="cfo-config-cancel-btn" onClick={() => setShowConfig(false)}>
                Cancel
              </button>
              <button type="button" className="cfo-config-save-btn" onClick={saveConfig} disabled={configSaving || configLoading}>
                <Save size={15} />
                {configSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CFODashboard;
